import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injectable,
  LOCALE_ID,
  OnDestroy,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, Router, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from "./app.routes";
import { providePrimeNG } from "primeng/config";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { environment } from "@environments/environment";
import { initializeTranslation, provideTranslation } from "@kernel/translations/translation.config";
import { TranslationService } from "@kernel/translations/translation.service";
import { ELocalizationLang } from "@shared-core/enums/localization-lang.enum";
import { LocalStorageService } from "@kernel/tools/services/local-storage.service";
import { registerLocaleData } from "@angular/common";
import { provideMessage } from "@kernel/tools/message/message.config";
import { provideSideNav } from "./shared/general/sidenav/providers/sidenav";
import { provideGraphQL } from "@kernel/graphql/graphql.config";
import { provideConfirmationDialog } from "@kernel/popups/popup/modules/confirmation-dialog/confirmation-dialog.config";
import { JwtModule } from "@auth0/angular-jwt";
import { ErrorInterceptor } from "./shared/general/interceptors/error.interceptor";
import { AuthService as BaseAuthService } from "./kernel/tools/services/auth.service";
import { AuthService } from "./shared/auth/services/auth.service";
import { ActiveDirectoryAbstraction } from "@shared-core/abstractions/active-directory.abstraction";
import { ActiveDirectoryService } from "./shared/auth/services/active-directory.service";
import { pendingRequestsInterceptor$ } from "ng-http-loader";
import { jwtInterceptor } from "@shared-core/interceptors/jwt.interceptor";
import { TopNavService } from "./shared/general/top-nav/services/top-nav.service";
import { TopNavAbstraction } from "@shared-core/abstractions/top-nav.abstraction";
import { laraTheme } from "@shared-core/config/primeng";
import { sentryUserContextInitializer } from "@shared-core/Initializers/sentry-user-context.Initializer";
import { sentryFeedbackInitializer } from "@shared-core/Initializers/sentry-feedback.Initializer";
import { sentryReportDialogInitializer } from "@shared-core/Initializers/sentry-report-dialog.Initializer";
import * as Sentry from "@sentry/angular";
import localeIt from "@angular/common/locales/it";

const locales = {
  en: () => import("@angular/common/locales/en"),
  it: () => import("@angular/common/locales/it"),
};
registerLocaleData(localeIt);
export const loadLocale = () => {
  const localStorage = inject(LocalStorageService);
  const lang = localStorage.get<ELocalizationLang>("language") || ELocalizationLang.EN;
  locales[lang]().then(d => registerLocaleData(d.default));
  return lang;
};

@Injectable()
class SentryErrorHandler implements ErrorHandler, OnDestroy {
  private sentryErrorHandler = Sentry.createErrorHandler();
  handleError(error: unknown): void {
    if (error === "Timeout") return;
    this.sentryErrorHandler.handleError(error);
  }
  ngOnDestroy(): void {
    this.sentryErrorHandler.ngOnDestroy();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      provideSideNav();
      return initializeTranslation(inject(TranslationService));
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      })
    ),
    provideMessage(),
    providePrimeNG({
      theme: {
        preset: laraTheme,
        options: {
          darkModeSelector: ".my-app-dark",
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useFactory: loadLocale,
      deps: [LocalStorageService],
    },
    provideGraphQL(environment.graphql_endpoint),
    provideTranslation(),
    provideConfirmationDialog(),
    importProvidersFrom(
      JwtModule.forRoot({
        config: { tokenGetter: _ => null },
      })
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: TopNavAbstraction,
      useExisting: TopNavService,
    },
    {
      provide: BaseAuthService,
      useExisting: AuthService,
    },
    {
      provide: ActiveDirectoryAbstraction,
      useExisting: ActiveDirectoryService,
    },
    provideHttpClient(withInterceptors([jwtInterceptor]), withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$])),
    { provide: ErrorHandler, useValue: new SentryErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => sentryUserContextInitializer()),
    provideAppInitializer(() => sentryFeedbackInitializer()),
    provideAppInitializer(() => sentryReportDialogInitializer()),
  ],
};
